import React, { useEffect, useState } from "react";
import Utility from "../../../helpers/Utility";
import DatePicker from "../../../components/datepicker";
import Select from "react-select";
import api from "../../../api";
import ruleAlertService from '../../../services/ruleAlertService';
import { Modal } from 'react-bootstrap';

interface CompSnoozePeriod {
  comp_type: string;
  seq: string;
  start_time: number | null;
  end_time: number | null;
}

interface RuleSnoozePeriod {
  code: string;
  start_time: number | null;
  end_time: number | null;
}

interface SelectedComponent {
  name: string;
  seq: string[];
}

interface DeleteRuleAlert {
  item: any;
  index: number;
}

export const RuleAlertSetup = () => {
  const [alerts, setAlerts] = useState<any>([]);
  const [assets, setAssets] = useState<any>([]);
  const [selectedAsset, setSelectedAsset] = useState(null);

  // component states
  const [componentSearchTerm, setComponentSearchTerm] = useState("");
  const [allComponents, setAllComponents] = useState<[]>([]);
  const [selectedComponents, setSelectedComponents] = useState<SelectedComponent[]>(() => {
    return allComponents
      .filter(c => c?.count > 0)
      .map(c => ({ 
        name: c.name, 
        seq: ["All"] // Initialize all with "All"
      })) ?? []; 
  });
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [opNotes, setOpNotes] = useState("");

  // Rule States
  const [ruleSearchTerm, setRuleSearchTerm] = useState("");
  const [selectedRules, setSelectedRules] = useState<string[]>([]);
  const [allRules, setAllRules] = useState<[]>([]);

  // Asset Filter
  const [assetFilter, setAssetFilter] = useState<string>('');
  const [compSnoozePeriods, setCompSnoozePeriods] = useState<CompSnoozePeriod[]>([]);
  const [ruleSnoozePeriods, setRuleSnoozePeriods] = useState<RuleSnoozePeriod[]>([]);

  // Delete Modal state
  const[deleteAlert, setDeleteAlert]= useState<DeleteRuleAlert>();
  const[deleteModalShow, setDeleteModalShow]= useState<boolean>(false);

  const onCloseDeleteModal = async() => {
    setDeleteAlert(undefined);
    setDeleteModalShow(false)
  }

  const getAllAssets = async () => {
    try {
      const data = await ruleAlertService.getAllAssets()
      
      console.log("ALL Assets: ", data);
      setAssets(data.assets);
      return data;
    } catch (err) {
      console.error("Error in getAllAlerts:", err);
      return [];
    }
  };

  const getAllAlerts = async (assetFilterVal: string) => {
    let assetFilterParam = ""
    if (assetFilterVal && assetFilterVal !== "") {
      assetFilterParam = "?asset_id=" + assetFilterVal;
    }
    try {
      const data = await ruleAlertService.getAllAlerts(assetFilterParam)

      // console.log("ALL Alerts: ", data);
      setAlerts(data.data);
      return data;
    } catch (err) {
      console.error("Error in getAllAlerts:", err);
      return [];
    }
  };

  useEffect(() => {
    getAllAssets();
    getAllAlerts("");
  }, []);

  // Sensor Functions
  const handleComponentSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setComponentSearchTerm(e.target.value);
  };

  const handleAddComponent = (comp: any) => {
    if (!selectedComponents.some(item => item.name === comp.name)) {
      setSelectedComponents([...selectedComponents, comp]);
    }
  };

  const handleRemoveComponent = (component: string) => {
    setSelectedComponents(selectedComponents.filter(comp => comp.name !== component));
  };

  const filteredSensors = [{name: "All", count: 0}, ...allComponents].filter(
    (component) =>
      component.name.toLowerCase().includes(componentSearchTerm.toLowerCase()) &&
      !selectedComponents.some(item => item.name === component.name)
  );

  // Rule Functions
  const handleRuleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRuleSearchTerm(e.target.value);
  };

  const handleAddRule = (rule: string) => {
    if (!selectedRules.includes(rule)) {
      setSelectedRules([...selectedRules, rule]);
    }
  };

  const handleRemoveRule = (rule: string) => {
    setSelectedRules(selectedRules.filter((item) => item !== rule));
  };

  const filteredRules = [{code: "All", description: ""}, ...allRules].filter(
    (rule) =>
      rule.code.toLowerCase().includes(ruleSearchTerm.toLowerCase()) &&
      !selectedRules.includes(rule.code)
  );

  const handleAssetFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedId = event.target.value;
    setAssetFilter(selectedId);
    
    getAllAlerts(selectedId);
  };

  const handleSetupAlert = async () => {
    setSelectedAsset(null);
    setOpNotes("");
    setAllComponents([]);
    setSelectedComponents([]);
    setAllRules([]);
    setSelectedRules([]);
    setRuleSnoozePeriods([]);
    setCompSnoozePeriods([]);
    setComponentSearchTerm("");
    setRuleSearchTerm("");

    Utility.showModal("showAlertModal");
  };
  
  const handleAssetChange = async(selected: any) => {
    setSelectedAsset(selected || []);

    if (!selected) return;
    try {
      const compData = await ruleAlertService.getAllAssetTypes()

      if (compData[selected.asset_type]) {
        const filterComponents = (components) => {
          // Filter components that are parents of more specific versions
          return components.filter(comp => {
            const isParentName = components.some(otherComp => 
              otherComp.name !== comp.name && 
              otherComp.name.startsWith(comp.name + '_')
            );
            return !isParentName;
          });
        };
        
        const filteredComponents = filterComponents(compData[selected.asset_type].components);
        setAllComponents(filteredComponents);
      }

      const rulesData = await ruleAlertService.getAllRulesByAsset(selected.asset_id)
      if (rulesData.rules) {
        setAllRules(rulesData.rules);
      }
    } catch (err) {
      console.log("Error Asset GET: ", err);
    }
  };

  // Update function for start time
  const handleStartTimeChange = (sensorName: string, date: Date | null) => {
    setCompSnoozePeriods(prev => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to 00:00:00

      const futureDate = new Date(today)
      futureDate.setDate(today.getDate() + 7); // Add 7 days

      if (date) {
        futureDate.setDate(date.getDate() + 7); // Add 7 days
      }

      const existingIndex = prev.findIndex(item => item.comp_type === sensorName);
      const newPeriod = {
        comp_type: sensorName,
        start_time: date ? date.getTime() : today.getTime(),
        end_time: existingIndex >= 0 ? prev[existingIndex].end_time : futureDate.getTime()
      };

      if (existingIndex >= 0) {
        return [
          ...prev.slice(0, existingIndex),
          newPeriod,
          ...prev.slice(existingIndex + 1)
        ];
      }
      return [...prev, newPeriod];
    });
  };

  // Update function for end time
  const handleEndTimeChange = (sensorName: string, date: Date | null) => {
    setCompSnoozePeriods(prev => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to 00:00:00

      const futureDate = new Date(today)
      futureDate.setDate(today.getDate() + 7); // Add 7 days

      const existingIndex = prev.findIndex(item => item.comp_type === sensorName);
      const newPeriod = {
        comp_type: sensorName,
        start_time: existingIndex >= 0 ? prev[existingIndex].start_time : today.getTime(),
        end_time: date ? date.getTime() : futureDate.getTime()
      };

      if (existingIndex >= 0) {
        return [
          ...prev.slice(0, existingIndex),
          newPeriod,
          ...prev.slice(existingIndex + 1)
        ];
      }
      return [...prev, newPeriod];
    });
  };

  //Update sequence
  const handleSeqChange = (event:any, comp: string) => {
    console.log(`Selected sequence for ${event.target.value}`);

    setCompSnoozePeriods(prev => {
      const existingIndex = prev.findIndex(item => item.comp_type === comp);
      const newPeriod = {
        comp_type: comp,
        seq: event.target.value,
        start_time: existingIndex >= 0 ? prev[existingIndex].start_time : null,
        end_time: existingIndex >= 0 ? prev[existingIndex].end_time : null
      };

      if (existingIndex >= 0) {
        return [
          ...prev.slice(0, existingIndex),
          newPeriod,
          ...prev.slice(existingIndex + 1)
        ];
      }
      return [...prev, newPeriod];
    });
  };

  const handleSelectChange = (compName: string, newSeqs: string[]) => {
    setSelectedComponents(prev => {
      const safeSeqs = newSeqs.length > 0 ? newSeqs : ["All"]; // Default to "All"
      const existingIndex = prev.findIndex(item => item.name === compName);
      
      if (existingIndex >= 0) {
        return prev.map((item, index) => 
          index === existingIndex 
            ? { ...item, seq: safeSeqs }
            : item
        );
      }
      return [...prev, { name: compName, seq: safeSeqs }];
    });
    setOpenDropdown(null);
  };

  const handleItemClick = (compName: string, itemValue: number) => {
    const currentSeqs = selectedComponents.find(sc => sc.name === compName)?.seq || [];
    const isAllSelected = currentSeqs.includes("All");
    const valueStr = String(itemValue);

    let newSeqs: string[];
    
    if (isAllSelected) {
      // If "All" was selected, replace it with this single selection
      newSeqs = [valueStr];
    } else if (currentSeqs.includes(valueStr)) {
      // If already selected, remove it
      newSeqs = currentSeqs.filter(v => v !== valueStr);
      // If nothing left selected, default to "All"
      if (newSeqs.length === 0) newSeqs = ["All"];
    } else {
      // Add to existing selections
      newSeqs = [...currentSeqs, valueStr];
    }

    handleSelectChange(compName, newSeqs);
  };

    // Get current values for a sensor
    const getSensorCompDates = (sensorName: string) => {
      const period = compSnoozePeriods.find(item => item.comp_type === sensorName);
      return {
        startDate: period?.start_time ? new Date(period.start_time) : null,
        endDate: period?.end_time ? new Date(period.end_time) : null,
        seq: period?.seq
      };
    };

  const handleRuleStartTimeChange = (code: string, date: Date | null) => {
    setRuleSnoozePeriods(prev => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to 00:00:00

      const futureDate = new Date(today)
      futureDate.setDate(today.getDate() + 7); // Add 7 days
      
      if (date) {
        futureDate.setDate(date.getDate() + 7);
      }

      const existingIndex = prev.findIndex(item => item.code === code);
      const newPeriod = {
        code: code,
        start_time: date ? date.getTime() : today.getTime(),
        end_time: existingIndex >= 0 ? prev[existingIndex].end_time : futureDate.getTime()
      };

      if (existingIndex >= 0) {
        return [
          ...prev.slice(0, existingIndex),
          newPeriod,
          ...prev.slice(existingIndex + 1)
        ];
      }
      return [...prev, newPeriod];
    });
  };

  // Update function for end time
  const handleRuleEndTimeChange = (code: string, date: Date | null) => {
    setRuleSnoozePeriods(prev => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to 00:00:00

      const futureDate = new Date(today)
      futureDate.setDate(today.getDate() + 7); // Add 7 days

      const existingIndex = prev.findIndex(item => item.code === code);
      const newPeriod = {
        code: code,
        start_time: existingIndex >= 0 ? prev[existingIndex].start_time : today.getTime(),
        end_time: date ? date.getTime() : futureDate.getTime()
      };

      if (existingIndex >= 0) {
        return [
          ...prev.slice(0, existingIndex),
          newPeriod,
          ...prev.slice(existingIndex + 1)
        ];
      }
      return [...prev, newPeriod];
    });
  };

    // Get current values for a sensor
    const getRuleDates = (code: string) => {
      const period = ruleSnoozePeriods.find(item => item.code === code);
      return {
        startDate: period?.start_time ? new Date(period.start_time) : null,
        endDate: period?.end_time ? new Date(period.end_time) : null
      };
    };

  async function handleAlertSave() {
    try {
      if (!selectedAsset) {
        console.log("No asset selected");
        return;
      }

      const snoozeComponents = compSnoozePeriods.map(item => {
        const matchedSelection = selectedComponents.find(
          selected => selected.name === item.comp_type
        );
        
        // Replace "All" with "*" for comp_type
        const processedCompType = item.comp_type === "All" ? "*" : item.comp_type;
        
        let cleanedSeq = matchedSelection?.seq?.map(seqVal =>
          seqVal === "All" ? "*" : seqVal
        );
    
        if (!cleanedSeq && matchedSelection?.count && matchedSelection?.count > 0) {
          cleanedSeq = ["*"];
        }
        
        return {
          ...item,
          comp_type: processedCompType,
          ...(matchedSelection && { seq: cleanedSeq })
        };
      });

      const processedRuleSnoozePeriods = ruleSnoozePeriods.map(item => {
        // Replace "All" with "*" for code
        const processedCode = item.code === "All" ? "*" : item.code;
        
        return {
            ...item,
            code: processedCode
        };
      });

      if (snoozeComponents.length === 0 && processedRuleSnoozePeriods.length === 0) {
        console.log("Snooze codes and components are empty!");
        return;
      }

      let payload = {
        asset_type: selectedAsset.asset_type,
        op_notes: opNotes || "",
        asset_id: selectedAsset.asset_id,
        snooze_components: snoozeComponents,
        snooze_codes: processedRuleSnoozePeriods
      }

      let response = await api.post('/rules',payload)
      const data  = response.data;
      
      // To load the changes made after save
      getAllAlerts("");

      // To close the modals
      Utility.closeModal("showAlertModal");
      Utility.closeModal("showDetailAlertModal");      
    } catch (err) {
      console.log("ERROR On Create Alert: ", err);
    }
  }

  async function onDeleteRule(): void {
    try {
      let item = deleteAlert?.item;
      const data = await ruleAlertService.deleteRuleAlert(item._id)

      // To load the changes made after save
      getAllAlerts(assetFilter);

      // hide the confirmation modal
      setDeleteModalShow(false)
    } catch (err) {
      console.log("ERROR On Delete Alert: ", err);
    }
  }

  function onDisplayDetailAlertModal(item: any) {
    setOpNotes(item.op_notes || "");
    setComponentSearchTerm("");
    setRuleSearchTerm("");

    Utility.showModal("showDetailAlertModal");
    const assetObj = assets.find(asset => asset.asset_id === item.asset_id);
    handleAssetChange(assetObj);
    
    if (item.snooze_components && item.snooze_components.length > 0) {  
      setSelectedComponents(
        item.snooze_components.map((comp: any) => ({
          name: comp.comp_type === "*" ? "All" : comp.comp_type,
          seq: comp.seq?.map((val: string) => (val === "*" ? "All" : val)) || ["All"]
        }))
      );
      
      setCompSnoozePeriods(
        item.snooze_components.map(comp => ({
          ...comp,
          comp_type: comp.comp_type === "*" ? "All" : comp.comp_type,
          seq: comp.seq?.map(val => val === "*" ? "All" : val) || ["All"]
        }))
      );    
    }

    if (item.snooze_codes && item.snooze_codes.length > 0) {
      const initialRules = item.snooze_codes.map((rule: any) => 
        rule.code === "*" ? "All" : rule.code
      );
      
      const uiFormattedRules = item.snooze_codes.map((rule: any) => ({
        ...rule,
        code: rule.code === "*" ? "All" : rule.code
      }));
    
      setSelectedRules(initialRules);
      setRuleSnoozePeriods(uiFormattedRules);
    }
  }

  const getCurrentSelection = (compName: string) => {
    const component = selectedComponents.find(sc => sc?.name === compName);
    if (!component || !component.seq || component.seq.includes("All")) {
      return "All";
    }
    return component.seq.filter(s => s !== "All").join(", ");
  };
  
  const dropdownMenuStyle = {
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 1000,
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#fff',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    minWidth: '120px',
    maxHeight: '200px',
    overflowY: 'auto'
  };
  
  const getItemStyle = (value: string, compName: string) => {
    const component = selectedComponents.find(sc => sc.name === compName);
    const isSelected = component 
      ? component.seq?.includes(value) ?? (value === "All" && !component.seq)
      : value === "All"; // Default to "All" selected if component not found
  
    return {
      padding: '6px 12px',
      backgroundColor: isSelected ? '#e6f7ff' : 'transparent',
      cursor: 'pointer',
      ':hover': {
        backgroundColor: '#f5f5f5'
      }
    };
  };


  return (
    <div className="container bs-docs-container">
      <div className="flex-row-space-between">
        <button
          className="btn btn-primary custom-button"
          onClick={async () => {
            await handleSetupAlert();
          }}
        >
          Setup Rule Alert
        </button>
        <select
          value={assetFilter}
          onChange={(e) => handleAssetFilterChange(e)}
          style={{ width: "200px" }}
        >
          <option value="">-- Show All Asset --</option>
          {assets.map((asset) => (
            <option
              key={asset.asset_type + ":" + asset.asset_id.toLowerCase()}
              value={asset.asset_id}
            >
              {asset.box_type == "grouped-box" ? asset.asset_name : asset.asset_id}
            </option>
          ))}
        </select>
      </div>
      <div className="alert-table">
        <table className="table table-striped border-success" style={{ width: "100%", display: "block", overflowX: "auto", whiteSpace: "nowrap" }}>
          <thead style={{ backgroundColor: '#4F8C92', color: 'white' }}>
            <tr>
              <th>Asset</th>
              <th>Op Notes</th>
              <th>Component</th>
              <th>Rule</th>
              <th>Last Update</th>
              <th>Last Updated By</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {alerts?.length > 0 ? (
              alerts?.map((alert: any, index: any) => (
                <tr
                  key={alert?._id}
                >
                  <td className="rules-code" onClick={() => onDisplayDetailAlertModal(alert)}>{alert?.asset_name || alert?.asset_id}</td>
                  <td 
                    style={{ 
                      maxWidth: "400px",
                      minWidth: "300px",
                      position: "relative"
                    }}
                    title={alert?.op_notes || "-"} // Tooltip showing full content
                  >
                    <div
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: "1.4em",
                        maxHeight: "2.8em",
                        whiteSpace: "normal",
                        wordBreak: "break-word"
                      }}
                    >
                      {alert?.op_notes || "-"}
                    </div>
                  </td>
                  <td 
                    style={{ 
                      maxWidth: "400px",
                      minWidth: "200px", // Ensure minimum width
                      position: "relative"
                    }}
                    title=  {alert?.snooze_components
                      ?.map((comp) => comp.comp_type)
                      .join(", ") || "-"}
                  >
                    <div
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        lineHeight: "1.4em",
                        maxHeight: "2.8em", // 2 lines * 1.4em line-height
                        whiteSpace: "normal",
                        wordBreak: "break-word"
                      }}
                    >
                      {alert?.snooze_components
                        ?.map((comp) => comp.comp_type)
                        .join(", ") || "-"}
                    </div>
                  </td>
                  <td>
                    {alert?.snooze_codes
                      ?.map((dCode) => dCode.code)
                      .join(", ") || "-"}
                  </td>
                  <td>
                    {alert?.updated_on
                      ? new Date(alert.updated_on).toLocaleDateString("en-US") : "-"}
                  </td>
                  <td>
                    {alert?.last_updated_by
                      ? alert.last_updated_by?.name || "-" : "-"}
                  </td>
                  <td
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      console.log("alert", alert);
                      setDeleteAlert({
                        item: alert,
                        index: index
                      });
                      setDeleteModalShow(true);
                    }}
                  >
                    <i className="fa fa-trash" aria-hidden="true"></i>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={6} style={{ textAlign: "center" }}>
                  No alerts setup found!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

     {/* showAlertModal */}
     <div
        className="modal fade bd-example-modal-lg"
        id="showAlertModal"
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg ">
          <div className="modal-content width-dynamic-alertModal">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                Add Rule Alert
              </h5>
              <span
                aria-hidden="true"
                className="close close-modal"
                aria-label="Close"
                onClick={() => {
                  Utility.closeModal("showAlertModal");
                }}
                style={{ cursor: "pointer" }}
              >
                &times;
              </span>
            </div>
            <div className="modal-body modal-text">
              <div>
                <div className="alert-form-group">
                  <label>Select Asset <span className="required">*</span> </label>
                  <Select
                    options={assets}
                    value={selectedAsset}
                    onChange={handleAssetChange}
                    placeholder="Select an Asset"
                    getOptionLabel={(e) => { return e.box_type == "grouped-box" ? e.asset_name : e.asset_id}}
                    getOptionValue={(e) => e.asset_type + ":" + e.asset_id.toLowerCase()}
                    // isClearable
                  />
                </div>

                <div className="alert-form-group">
                  <label className="sensor-box-label">
                    Op Notes
                  </label>
                  <textarea
                    className="form-control"
                    id="op_notes"
                    placeholder="Operations Notes for rule."
                    value={opNotes}
                    onChange={(e: any) => setOpNotes(e.target.value)}
                    maxLength={400}
                    rows={3}
                    style={{
                      resize: 'vertical',
                      overflow: 'auto'
                    }}
                  />
                  <div className="text-muted small mt-1">
                    {opNotes.length}/400 characters
                  </div>
                </div>

                <div className="sensor-box-container">

                  <div className="sensor-box-left">
                    <label className="sensor-box-label">
                      Select Component
                    </label>
                    {/* Selected sensors above the search box */}
                    <div className="sensor-box-selected-top">
                      {selectedComponents.map((comp) => {
                        const { startDate, endDate } = getSensorCompDates(comp.name);

                        return (
                          <div
                            key={comp.name}
                            className="sensor-box-selected-with-dates"
                          >

                            <div style={{ display: "flex",flexDirection:'column' }} className="width-dynamic-30">
                             <div className="sensor-name">
                              {comp.name}
                            </div>
                            {allComponents.find((c) => c.name === comp.name)?.count > 0 && (
                              <div style={{ display: "flex", alignItems: "center", marginTop: '8px' }}>
                                <p className="sensor-name" style={{ marginRight: '8px', minWidth: '40px' }}>Seq:</p>
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                  {/* Custom dropdown button */}
                                  <button
                                    style={{
                                      border: '1px solid #ccc',
                                      borderRadius: '4px',
                                      padding: '6px 12px',
                                      minWidth: '120px',
                                      textAlign: 'left',
                                      backgroundColor: '#fff',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}
                                    onClick={() => setOpenDropdown(openDropdown === comp.name ? null : comp.name)}
                                  >
                                    <span>
                                      {getCurrentSelection(comp.name)}
                                    </span>
                                    <span style={{ marginLeft: '8px' }}>▼</span>
                                  </button>

                                  {/* Dropdown menu */}
                                  {openDropdown === comp.name && (
                                    <div style={dropdownMenuStyle}>
                                      <div
                                        style={getItemStyle("All", comp.name)}
                                        onClick={() => handleSelectChange(comp.name, ["All"])}
                                      >
                                        All
                                      </div>
                                      {Array.from({ length: allComponents.find(c => c.name === comp.name)?.count || 0 }, (_, i) => (
                                        <div
                                          key={i + 1}
                                          style={getItemStyle(String(i + 1), comp.name)}
                                          onClick={() => handleItemClick(comp.name, i + 1)}
                                        >
                                          {i + 1}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="alert-form-group-50">
                            <label>Snooze From</label>
                            <DatePicker
                              placeholderText="Choose date"
                              value={startDate}
                              dateFormat="MM/dd/yyyy"
                              minDate={new Date()}
                              maxDate={new Date(new Date().setMonth(new Date().getMonth() + 1))}
                              isClearable={true}
                              onChange={(value: any) => {
                                handleStartTimeChange(comp.name, value)
                              }}
                              disabled={false}
                            />
                          </div>
                          <div className="alert-form-group-50">
                            <label>Snooze Till <span className="required">*</span> </label>
                            <DatePicker
                              placeholderText="Choose date"
                              value={endDate}
                              dateFormat="MM/dd/yyyy"
                              minDate={new Date()}
                              maxDate={new Date(new Date().setMonth(new Date().getMonth() + 12))}
                              isClearable={true}
                              onChange={(value: any) => {
                                handleEndTimeChange(comp.name, value)
                              }}
                              disabled={false}
                            />
                          </div>
                          <span
                            className="sensor-box-remove"
                            onClick={() => {
                              handleRemoveComponent(comp.name);
                              setCompSnoozePeriods(prev => prev.filter(item => item.comp_type !== comp.name));
                            }}
                          >
                            ✕
                          </span>
                        </div>
                        );
                      })}
                    </div>


                    {/* Search input and list */}
                    <input
                      type="text"
                      placeholder="Search"
                      value={componentSearchTerm}
                      onChange={handleComponentSearchChange}
                      className="sensor-box-search"
                    />
                    <div className="sensor-box-list">
                      {filteredSensors.map((component) => (
                        <div
                          key={component.name}
                          className="sensor-box-item"
                          onClick={() => handleAddComponent(component)}
                        >
                          <span className="sensor-box-plus">+</span> {component.name}
                        </div>
                      ))}
                    </div>
                  </div>


                  {/* Rule Section */}
                  <label className="sensor-box-label" style={{ marginTop: "24px" }}>
                    Select Rule <span className="required">*</span>
                  </label>

                  <div className="sensor-box-left">
                    {/* Selected rules displayed above the search box */}
                    <div className="sensor-box-selected-top">
                        {selectedRules.map((rule) => {
                          const { startDate, endDate } = getRuleDates(rule);
  
                          return (
                            <div key={rule} className="sensor-box-selected-with-dates">
                              <div className="sensor-name">
                                {rule}
                        
                              </div>
                              <div className="alert-form-group-50">
                                <label>Snooze From</label>
                                <DatePicker
                                  placeholderText="Choose date"
                                  value={startDate}
                                  dateFormat="MM/dd/yyyy"
                                  minDate={new Date()}
                                  maxDate={new Date(new Date().setMonth(new Date().getMonth() + 1))}
                                  isClearable={true}
                                  onChange={(value: any) => {
                                    handleRuleStartTimeChange(rule, value)
                                  }}
                                  disabled={false}
                                />
                              </div>
                              <div className="alert-form-group-50">
                                <label>Snooze Till <span className="required">*</span> </label>
                                <DatePicker
                                  placeholderText="Choose date"
                                  value={endDate}
                                  dateFormat="MM/dd/yyyy"
                                  minDate={new Date()}
                                  maxDate={new Date(new Date().setMonth(new Date().getMonth() + 12))}
                                  isClearable={true}
                                  onChange={(value: any) => {
                                    console.log(value);
                                    handleRuleEndTimeChange(rule, value)
                                  }}
                                  disabled={false}
                                />
                              </div>
                              <span
                                  className="sensor-box-remove"
                                  onClick={() => {
                                    handleRemoveRule(rule);
                                    setRuleSnoozePeriods(prev => prev.filter(item => item.code !== rule));
                                  }}
                                >
                                  ✕
                              </span>
                            </div>
                          );
                        })}
                    </div>

                    {/* Search box and list */}
                    <input
                      type="text"
                      placeholder="Search rule"
                      value={ruleSearchTerm}
                      onChange={handleRuleSearchChange}
                      className="sensor-box-search"
                    />
                    <div className="sensor-box-list">
                      {filteredRules.map((rule) => (
                        <div
                          key={rule.code}
                          className="sensor-box-item"
                          onClick={() => handleAddRule(rule.code)}
                        >
                          <span className="sensor-box-plus">+</span> {rule.code}
                          <div style={{marginLeft:18}}>{rule.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-end">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleAlertSave();
                }}
              >
                Save
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  Utility.closeModal("showAlertModal");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* showDetailAlertModal */}
      <div
        className="modal fade bd-example-modal-lg"
        id="showDetailAlertModal"
        role="dialog"
        aria-labelledby="myLargeModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg ">
          <div className="modal-content width-dynamic-alertModal">
            <div className="modal-header">
              <h5 className="modal-title" id="exampleModalLabel">
                Rule Alert
              </h5>
              <span
                aria-hidden="true"
                className="close close-modal"
                aria-label="Close"
                onClick={() => {
                  Utility.closeModal("showDetailAlertModal");
                }}
                style={{ cursor: "pointer" }}
              >
                &times;
              </span>
            </div>
            <div className="modal-body modal-text">
              <div>
                <div className="alert-form-group">
                  <label>
                    Select Asset <span className="required">*</span>{" "}
                  </label>
                  <Select
                    options={assets}
                    value={selectedAsset}
                    onChange={handleAssetChange}
                    placeholder="Select an Asset"
                    getOptionLabel={(e) => {return e.box_type == "grouped-box" ? e.asset_name : e.asset_id}}
                    getOptionValue={(e) =>
                      e.asset_type + ":" + e.asset_id.toLowerCase()
                    }
                  />
                </div>

                <div className="alert-form-group">
                  <label className="sensor-box-label">
                    Op Notes
                  </label>
                  <textarea
                    className="form-control"
                    id="op_notes"
                    placeholder="Operations Notes for rule."
                    value={opNotes}
                    onChange={(e: any) => setOpNotes(e.target.value)}
                    maxLength={400}
                    rows={3}
                    style={{
                      resize: 'vertical',
                      overflow: 'auto'
                    }}
                  />
                  <div className="text-muted small mt-1">
                    {opNotes.length}/400 characters
                  </div>
                </div>

                <div className="sensor-box-container">
                  <div className="sensor-box-left">
                    <label className="sensor-box-label">Select Component</label>
                    {/* Selected sensors above the search box */}
                    <div className="sensor-box-selected-top">
                      {selectedComponents.map((comp) => {
                        const { startDate, endDate, seq } = getSensorCompDates(
                          comp.name
                        );

                        return (
                          
                          <div
                            key={comp.name}
                            className="sensor-box-selected-with-dates"
                          >
                            <div style={{ display: "flex",flexDirection:'column' }} className="width-dynamic-30">
                             <div className="sensor-name">
                            {comp.name}
                            </div>
                            {allComponents.find((c) => c.name === comp.name)?.count > 0 && (
                              <div style={{ display: "flex", alignItems: "center", marginTop: '8px' }}>
                                <p className="sensor-name" style={{ marginRight: '8px', minWidth: '40px' }}>Seq:</p>
                                <div style={{ position: 'relative', display: 'inline-block' }}>
                                  {/* Custom dropdown button */}
                                  <button
                                    style={{
                                      border: '1px solid #ccc',
                                      borderRadius: '4px',
                                      padding: '6px 12px',
                                      minWidth: '120px',
                                      textAlign: 'left',
                                      backgroundColor: '#fff',
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center'
                                    }}
                                    onClick={() => setOpenDropdown(openDropdown === comp.name ? null : comp.name)}
                                  >
                                    <span>
                                      {getCurrentSelection(comp.name)}
                                    </span>
                                    <span style={{ marginLeft: '8px' }}>▼</span>
                                  </button>

                                  {/* Dropdown menu */}
                                  {openDropdown === comp.name && (
                                    <div style={dropdownMenuStyle}>
                                      <div
                                        style={getItemStyle("All", comp.name)}
                                        onClick={() => handleSelectChange(comp.name, ["All"])}
                                      >
                                        All
                                      </div>
                                      {Array.from({ length: allComponents.find(c => c.name === comp.name)?.count || 0 }, (_, i) => (
                                        <div
                                          key={i + 1}
                                          style={getItemStyle(String(i + 1), comp.name)}
                                          onClick={() => handleItemClick(comp.name, i + 1)}
                                        >
                                          {i + 1}
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}
                          
                          </div>
                        
                            <div className="alert-form-group-50">
                              <label>Snooze From</label>
                              <DatePicker
                                placeholderText="Choose date"
                                value={startDate}
                                dateFormat="MM/dd/yyyy"
                                minDate={new Date()}
                                maxDate={
                                  new Date(
                                    new Date().setMonth(
                                      new Date().getMonth() + 1
                                    )
                                  )
                                }
                                isClearable={true}
                                onChange={(value: any) => {
                                  handleStartTimeChange(comp.name, value);
                                }}
                                disabled={false}
                              />
                            </div>
                            <div className="alert-form-group-50">
                              <label>
                                Snooze Till <span className="required">*</span>{" "}
                              </label>
                              <DatePicker
                                placeholderText="Choose date"
                                value={endDate}
                                dateFormat="MM/dd/yyyy"
                                minDate={new Date()}
                                maxDate={
                                  new Date(
                                    new Date().setMonth(
                                      new Date().getMonth() + 12
                                    )
                                  )
                                }
                                isClearable={true}
                                onChange={(value: any) => {
                                  handleEndTimeChange(comp.name, value);
                                }}
                                disabled={false}
                              />
                            </div>
                            <span
                              className="sensor-box-remove"
                              onClick={() => {
                                handleRemoveComponent(comp.name);
                                setCompSnoozePeriods((prev) =>
                                  prev.filter(
                                    (item) => item.comp_type !== comp.name
                                  )
                                );
                              }}
                            >
                              ✕
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Search input and list */}
                    <input
                      type="text"
                      placeholder="Search"
                      value={componentSearchTerm}
                      onChange={handleComponentSearchChange}
                      className="sensor-box-search"
                    />
                    <div className="sensor-box-list">
                      {filteredSensors.map((component) => (
                        <div
                          key={component.name}
                          className="sensor-box-item"
                          onClick={() => handleAddComponent(component)}
                        >
                          <span className="sensor-box-plus">+</span> {component.name}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Rule Section */}
                  <label
                    className="sensor-box-label"
                    style={{ marginTop: "24px" }}
                  >
                    Select Rule <span className="required">*</span>
                  </label>

                  <div className="sensor-box-left">
                    {/* Selected rules displayed above the search box */}
                    <div className="sensor-box-selected-top">
                      {selectedRules.map((rule) => {
                        const { startDate, endDate } = getRuleDates(rule);

                        return (
                          <div
                            key={rule}
                            className="sensor-box-selected-with-dates"
                          >
                            <div className="sensor-name" >{rule}</div>
                            <div className="alert-form-group-50">
                              <label>Snooze From</label>
                              <DatePicker
                                placeholderText="Choose date"
                                value={startDate}
                                dateFormat="MM/dd/yyyy"
                                minDate={new Date()}
                                maxDate={
                                  new Date(
                                    new Date().setMonth(
                                      new Date().getMonth() + 1
                                    )
                                  )
                                }
                                isClearable={true}
                                onChange={(value: any) => {
                                  handleRuleStartTimeChange(rule, value);
                                }}
                                disabled={false}
                              />
                            </div>
                            <div className="alert-form-group-50">
                              <label>
                                Snooze Till <span className="required">*</span>{" "}
                              </label>
                              <DatePicker
                                placeholderText="Choose date"
                                value={endDate}
                                dateFormat="MM/dd/yyyy"
                                minDate={new Date()}
                                maxDate={
                                  new Date(
                                    new Date().setMonth(
                                      new Date().getMonth() + 12
                                    )
                                  )
                                }
                                isClearable={true}
                                onChange={(value: any) => {
                                  console.log(value);
                                  handleRuleEndTimeChange(rule, value);
                                }}
                                disabled={false}
                              />
                            </div>
                            <span
                              className="sensor-box-remove"
                              onClick={() => {
                                handleRemoveRule(rule);
                                setRuleSnoozePeriods((prev) =>
                                  prev.filter((item) => item.code !== rule)
                                );
                              }}
                            >
                              ✕
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    {/* Search box and list */}
                    <input
                      type="text"
                      placeholder="Search rule"
                      value={ruleSearchTerm}
                      onChange={handleRuleSearchChange}
                      className="sensor-box-search"
                    />
                    <div className="sensor-box-list">
                      {filteredRules.map((rule) => (
                        <div
                          key={rule.code}
                          className="sensor-box-item"
                          onClick={() => handleAddRule(rule.code)}
                        >
                          <span className="sensor-box-plus">+</span> {rule.code}
                          <div style={{marginLeft:18}}>{rule.description}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer d-flex justify-content-end">
              <button
                className="btn btn-primary"
                onClick={() => {
                  handleAlertSave();
                }}
              >
                Save
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  Utility.closeModal("showDetailAlertModal");
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>


      {/* Delete Modal */}
      {deleteAlert && (
        <Modal
            show={deleteModalShow}
            onHide={() => onCloseDeleteModal()}
        >
            <Modal.Header closeButton>
                Delete Alert
            </Modal.Header>
            <Modal.Body>
                <div className="modal-body modal-text">
                Are you sure you want to delete the alert for <strong>{deleteAlert.item?.asset_name || "this item"}</strong> ?
                </div>
                <div className="modal-footer pb-0">
                    <button type="button" className="btn btn-primary" data-dismiss="modal" onClick={() => onCloseDeleteModal()}>Cancel</button>
                    <button
                        onClick={() => onDeleteRule()}
                        type="button"
                        className="btn btn-secondary"                          
                        >
                        Delete
                    </button>
                </div>
            </Modal.Body>
        </Modal>
      )}
    </div>
  );
};
